/**
 * Heatmap Import View
 * Upload CSV-fil och visualisera i heatmap
 */

import React, { useState, useRef } from 'react';
import { useTranslation } from '@/store/languageStore';
import { heatmapImportService, ImportResult } from '@/services/heatmapImport/heatmapImportService';
import toast from 'react-hot-toast';
import { ArrowUpTrayIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { PageBackButton } from '@/components/common/PageBackButton';
import Heatmap from './Heatmap';

const HeatmapImport: React.FC = () => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    // Validera filtyp
    if (!file.name.endsWith('.csv') && file.type !== 'text/csv') {
      toast.error(t('heatmapImport.invalidFileType') || 'Endast CSV-filer är tillåtna');
      return;
    }

    // Validera filstorlek (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      toast.error(t('heatmapImport.fileTooLarge') || 'Filen är för stor (max 100MB)');
      return;
    }

    setUploading(true);
    const loadingToast = toast.loading(t('heatmapImport.uploading') || 'Laddar upp CSV-fil...');

    try {
      const result = await heatmapImportService.uploadCSV(file);
      setImportResult(result);
      setSessionId(result.sessionId);
      
      toast.dismiss(loadingToast);
      toast.success(
        t('heatmapImport.uploadSuccess')
          ?.replace('{valid}', String(result.validRows))
          ?.replace('{total}', String(result.totalRows)) ||
        `${result.validRows} av ${result.totalRows} rader importerades`
      );

      if (result.invalidRows > 0) {
        toast.error(
          t('heatmapImport.invalidRows')?.replace('{count}', String(result.invalidRows)) ||
          `${result.invalidRows} rader kunde inte importeras`
        );
      }
    } catch (error: any) {
      toast.dismiss(loadingToast);
      toast.error(
        error.message || 
        t('heatmapImport.uploadError') || 
        'Kunde inte ladda upp filen'
      );
    } finally {
      setUploading(false);
      // Rensa file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleClear = async () => {
    if (!sessionId) {
      return;
    }

    try {
      await heatmapImportService.clearSession(sessionId);
      setImportResult(null);
      setSessionId(null);
      toast.success(t('heatmapImport.cleared') || 'Import-data rensad');
    } catch (error: any) {
      toast.error(error.message || t('heatmapImport.clearError') || 'Kunde inte rensa data');
    }
  };

  return (
    <div className="h-full flex flex-col bg-bgContainer">
      {/* Upload-sektion */}
      <div className="p-4 bg-bgElevated border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <PageBackButton />
            <h1 className="text-2xl font-bold text-textDefault flex items-center gap-2">
              <ArrowUpTrayIcon className="w-6 h-6 text-primary" />
              {t('heatmapImport.title') || 'Heatmap Import'}
            </h1>
          </div>
          {sessionId && (
            <button
              onClick={handleClear}
              className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              {t('heatmapImport.clear') || 'Rensa'}
            </button>
          )}
        </div>

        {!sessionId ? (
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              onChange={handleFileSelect}
              className="hidden"
              disabled={uploading}
            />
            <ArrowUpTrayIcon className="w-12 h-12 mx-auto text-textSecondary mb-4" />
            <p className="text-textDefault mb-2">
              {t('heatmapImport.selectFile') || 'Välj CSV-fil att importera'}
            </p>
            <p className="text-sm text-textSecondary mb-4">
              {t('heatmapImport.fileFormat') || 'Format: timestamp;latitude;longitude;registration_number;speed;speed_limit'}
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-4 py-2 bg-bgPrimary text-white rounded hover:bg-bgPrimary/90 disabled:opacity-50"
            >
              {uploading
                ? (t('heatmapImport.uploading') || 'Laddar upp...')
                : (t('heatmapImport.selectFileButton') || 'Välj fil')}
            </button>
          </div>
        ) : (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-textDefault">
                  {t('heatmapImport.importComplete') || 'Import klar'}
                </p>
                <p className="text-sm text-textSecondary mt-1">
                  {t('heatmapImport.importStats')
                    ?.replace('{valid}', String(importResult?.validRows || 0))
                    ?.replace('{total}', String(importResult?.totalRows || 0))
                    ?.replace('{vehicles}', String(importResult?.vehicles.length || 0)) ||
                    `${importResult?.validRows || 0} punkter, ${importResult?.vehicles.length || 0} fordon`}
                </p>
              </div>
              <button
                onClick={() => {
                  setImportResult(null);
                  setSessionId(null);
                }}
                className="p-2 hover:bg-bgElevated rounded"
              >
                <XMarkIcon className="w-5 h-5 text-textSecondary" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Heatmap-visualisering (använd samma komponent men med import-data) */}
      {sessionId && (
        <div className="flex-1">
          <Heatmap sessionId={sessionId} isImport={true} />
        </div>
      )}
    </div>
  );
};

export default HeatmapImport;

